
import { computed, defineComponent, reactive } from 'vue';
import Card from '@/core/components/Tela/Card.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import {
  IProdutoAditivo, IProdutoCombustivel, IProdutoComponenteNutricional, IProdutoMedicamento,
  IProdutoRegistroMapa,
} from '@/models/Entidades/Cadastros/Produtos/IProduto';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { useTelaBase } from '@/core/composables/TelaBase';
import Icone from '@/core/components/Icone.vue';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';

export default defineComponent({
  name: 'ProdutoDadosEspecificos',
  props: {
    medicamento: {
      type: Boolean,
      required: true,
    },
    produtoMedicamento: {
      type: Object as () => IProdutoMedicamento,
      required: true,
    },
    combustivel: {
      type: Boolean,
      required: true,
    },
    produtoCombustivel: {
      type: Object as () => IProdutoCombustivel,
      required: true,
    },
    aditivo: {
      type: Boolean,
      required: true,
    },
    aditivoDetalhe: {
      type: Object as () => IProdutoAditivo,
      required: true,
    },
    componenteNutricional: {
      type: Boolean,
      required: true,
    },
    componentesNutricionais: {
      type: Array as () => IProdutoComponenteNutricional[],
      required: true,
    },
    registroMapa: {
      type: Boolean,
      required: true,
    },
    registroMapaDetalhe: {
      type: Object as () => IProdutoRegistroMapa,
      required: true,
    },
  },
  components: {
    Card,
    CampoNumerico,
    Icone,
  },
  emits: ['update:codigoPlanoContaCategoria', 'update:medicamento', 'update:produtoMedicamento',
    'update:combustivel', 'update:produtoCombustivel', 'update:aditivo', 'update:aditivoDetalhe',
    'update:componenteNutricional', 'update:componentesNutricionais', 'update:registroMapa', 'update:registroMapaDetalhe'],
  setup(props, { emit }) {
    const state = reactive({
      nome: '',
      percentual: 0,
      indexSelecionado: -1,
    });

    const {
      gradeBase,
    } = useGradeBase();

    const { apresentarMensagemAlerta } = useTelaBase();

    function preencherColunas() {
      gradeBase.colunas = [
        {
          title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
        },
        {
          title: 'Nome Componente', dataIndex: 'nomeComponente', key: 'nomeComponente', position: 1, visible: true,
        },
        {
          title: 'Percentual', dataIndex: 'percentual', key: 'Percentual', position: 2, visible: true, align: 'center', customRenderRow: ECustomRenderRow.MascararFracionado,
        },
        {
          title: 'Açoes', key: 'acoes', position: 3, visible: true, fixed: 'right', width: 80, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    const computedMedicamento = computed({
      get: () => props.medicamento,
      set: (valor: boolean) => {
        emit('update:medicamento', valor);
      },
    });

    const computedProdutoMedicamento = computed({
      get: () => props.produtoMedicamento,
      set: (valor: IProdutoMedicamento) => {
        emit('update:produtoMedicamento', valor);
      },
    });

    const computedCombustivel = computed({
      get: () => props.combustivel,
      set: (valor: boolean) => {
        emit('update:combustivel', valor);
      },
    });

    const computedProdutoCombustivel = computed({
      get: () => props.produtoCombustivel,
      set: (valor: IProdutoCombustivel) => {
        emit('update:produtoCombustivel', valor);
      },
    });

    const computedAditivo = computed({
      get: () => props.aditivo,
      set: (valor: boolean) => {
        emit('update:aditivo', valor);
      },
    });

    const computedAditivoDetalhe = computed({
      get: () => props.aditivoDetalhe,
      set: (valor: IProdutoAditivo) => {
        emit('update:aditivoDetalhe', valor);
      },
    });

    const computedComponenteNutricional = computed({
      get: () => props.componenteNutricional,
      set: (valor: boolean) => {
        emit('update:componenteNutricional', valor);
      },
    });

    const computedComponentesNutricionais = computed({
      get: () => props.componentesNutricionais,
      set: (valor: IProdutoComponenteNutricional[]) => {
        emit('update:componentesNutricionais', valor);
      },
    });

    const computedRegistroMapa = computed({
      get: () => props.registroMapa,
      set: (valor: boolean) => {
        emit('update:registroMapa', valor);
      },
    });

    const computedRegistroMapaDetalhe = computed({
      get: () => props.registroMapaDetalhe,
      set: (valor: IProdutoRegistroMapa) => {
        emit('update:registroMapaDetalhe', valor);
      },
    });

    function editarItem(index: number) {
      state.indexSelecionado = index;
      state.nome = computedComponentesNutricionais.value[index].nomeComponente;
      state.percentual = computedComponentesNutricionais.value[index].percentual;
    }

    function adicionarItem(index: number) {
      if (index > -1) {
        computedComponentesNutricionais.value[index].nomeComponente = state.nome;
        computedComponentesNutricionais.value[index].percentual = state.percentual;
      } else {
        if (!UtilitarioGeral.valorValido(state.nome)) {
          apresentarMensagemAlerta('Por favor, defina um valor para o compomente!');
          return;
        }
        const compomente:IProdutoComponenteNutricional = {} as IProdutoComponenteNutricional;
        compomente.codigo = 0;
        compomente.codigoProduto = 0;
        compomente.nomeComponente = state.nome;
        compomente.percentual = state.percentual;
        computedComponentesNutricionais.value.push(compomente);
      }

      state.nome = '';
      state.percentual = 0;
      state.indexSelecionado = -1;
    }
    function removerItem(index: number) {
      computedComponentesNutricionais.value.splice(index, 1);
    }

    preencherColunas();

    return {
      props,
      state,
      computedMedicamento,
      computedProdutoMedicamento,
      computedCombustivel,
      computedProdutoCombustivel,
      computedAditivo,
      computedAditivoDetalhe,
      computedComponenteNutricional,
      computedComponentesNutricionais,
      computedRegistroMapa,
      computedRegistroMapaDetalhe,
      adicionarItem,
      removerItem,
      gradeBase,
      ECustomRenderRow,
      UtilitarioMascara,
      editarItem,
    };
  },
});
